import { useEffect } from 'react'
import { useFeatureToggleStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'

/**
 * Returns whether a feature toggle is enabled for the current user.
 *
 * Reads from the store's per-name map, which is populated by a single
 * `featureTogglesByName` request (accessible to any authenticated user).
 * AppLayout front-loads it on auth; this hook also triggers the load so the
 * toggle resolves even if a component mounts in isolation. Returns `false`
 * until loaded, then re-renders with the real value — components reading this
 * must be wrapped in `observer()`.
 *
 * @param toggleName - The name of the feature toggle
 */
export function useFeatureToggle(toggleName: string): boolean {
  return useFeatureToggleState(toggleName).enabled
}

/**
 * Like `useFeatureToggle`, plus `settled`: true once the toggle request has
 * answered, success or failure. A route guard that redirects on a disabled
 * toggle must wait for `settled`, because `enabled` is `false` before the
 * toggles load (TRK-5546). Components reading this must be wrapped in
 * `observer()`.
 *
 * @param toggleName - The name of the feature toggle
 */
export function useFeatureToggleState(toggleName: string): { enabled: boolean; settled: boolean } {
  const store = useFeatureToggleStore()

  useEffect(() => {
    bg(() => store.load())
  }, [store])

  return { enabled: !!toggleName && store.isEnabled(toggleName), settled: store.loadSettled }
}
